import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  getAllOrders,
  getOrderByAdmin,
  updateDispatchStatus,
  updateDropOffDetails,
  updateDropOffLocation,
  getAllDisputeOrders, // Import the new API
  getDisputeReasons, // Import the new API
  getDisputeReasonById,
  reviewDispute,
  callFinalDecision,
  getSingleDispute,
  completeCourierPayment,
  getOrderSummary,
  getAllReturnedOrders,
  getAllExecutedOrders,
  getOrdersInTransit,
  updateBulkDropOffDetails,
} from "../../apis/order";
import { toast } from "react-toastify";
import { cleanObject } from "../../helpers";
import { Order, OrderData, OrderStats } from "./types/orderTypes";

interface OrdersState {
  orders: OrderData | null;
  orderSummary: OrderStats | null;
  selectedOrder: Order | null;
  selectedDispute: any;
  selectedReturnedOrder: any;
  disputeReasons: any;
  disputeOrders: any;
  returnedOrders: any;
  executedOrders: any;
  ordersInTransit: any;
  disputeReasonDetails: any;
  loading: boolean;
  error: string | null;
  currentTable: number;
}

// Initial state for the slice
const initialState: OrdersState = {
  orders: null,
  orderSummary: null,
  selectedOrder: null,
  selectedDispute: null,
  selectedReturnedOrder: null,
  disputeReasons: [],
  disputeOrders: [],
  returnedOrders: [],
  executedOrders: [],
  ordersInTransit: [],
  disputeReasonDetails: null,
  loading: false,
  error: null,
  currentTable: 1,
};

// Async thunk for fetching all orders
export const fetchAllOrders = createAsyncThunk(
  "orders/fetchAll",
  async (parameters: any, { rejectWithValue }) => {
    const params = cleanObject(parameters);
    try {
      const data = await getAllOrders(params);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data ?? "Failed to fetch orders");
    }
  }
);

export const fetchOrderSummaryAsync = createAsyncThunk(
  "orders/fetchOrderSummary",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getOrderSummary();
      return res?.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data ?? "Failed to fetch orders summary"
      );
    }
  }
);

// Async thunk for fetching a specific order by ID
export const fetchOrderById = createAsyncThunk(
  "orders/fetchById",
  async (orderId: any, { rejectWithValue }) => {
    try {
      const res = await getOrderByAdmin(orderId);
      return res?.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data ?? "Failed to fetch order");
    }
  }
);

// Async thunk for fetching a specific dispute by ID
export const fetchSingleDispute = createAsyncThunk(
  "orders/fetchDisputeById",
  async (disputeId: any, { rejectWithValue }) => {
    try {
      const data = await getSingleDispute(disputeId);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data ?? "Failed to fetch dispute");
    }
  }
);

// Async thunk for updating drop-off details
export const updateOrderDropOff = createAsyncThunk(
  "orders/updateDropOff",
  async (
    { contractId, dropOfDetails }: { contractId: string; dropOfDetails: any },
    { rejectWithValue }
  ) => {
    try {
      const data = await updateDropOffDetails(contractId, dropOfDetails);
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data ?? "Failed to update drop-off details"
      );
    }
  }
);

// Async thunk for updating drop-off details
export const updateBulkOrderDropOff = createAsyncThunk(
  "orders/updateBulkOrderDropOff",
  async (
    {agentId, dropOfDetails }: { agentId: string; dropOfDetails: any },
    { rejectWithValue }
  ) => {
    try {
      const data = await updateBulkDropOffDetails(agentId, dropOfDetails);
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data ?? "Failed to update drop-off details"
      );
    }
  }
);

// Async thunk for updating drop-off location
export const updateOrderLocation = createAsyncThunk(
  "orders/updateOrderLocation",
  async (
    { orderId, locationDetails }: { orderId: string; locationDetails: any },
    { rejectWithValue }
  ) => {
    try {
      const data = await updateDropOffLocation(orderId, locationDetails);
      toast.success("Order dispatched successfully");
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data ?? "Failed to update drop-off details"
      );
    }
  }
);

// Async thunk for updating dispatch status
export const updateDispatchOrderStatus = createAsyncThunk(
  "orders/updateDispatchOrderStatus",
  async (dispatchDetails: any, { rejectWithValue }) => {
    try {
      const data = await updateDispatchStatus(dispatchDetails);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data ?? "Failed to update status");
    }
  }
);

// Async thunk for fetching all dispute orders
export const fetchAllDisputeOrders = createAsyncThunk(
  "orders/fetchAllDisputeOrders",
  async (parameters: any, { rejectWithValue }) => {
    try {
      const data = await getAllDisputeOrders(parameters);
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data ?? "Failed to fetch dispute orders"
      );
    }
  }
);

// Async thunk for fetching all executed orders
export const fetchAllExecutedOrdersAsync = createAsyncThunk(
  "orders/fetchAllExecutedOrders",
  async (parameters: any, { rejectWithValue }) => {
    try {
      const data = await getAllExecutedOrders(parameters);
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data ?? "Failed to fetch dispute orders"
      );
    }
  }
);
// Async thunk for fetching all dispute orders
export const fetchAllOrdersInTransitAsync = createAsyncThunk(
  "orders/fetchAllOrdersInTransit",
  async (parameters: any, { rejectWithValue }) => {
    const params = {
      limit: parameters.limit,
      page: parameters.page,
    };
    try {
      const res = await getOrdersInTransit(
        params,
        parameters?.courierOfficeId
      );
      return res?.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data ?? "Failed to fetch dispute orders"
      );
    }
  }
);
// Async thunk for fetching all dispute orders
export const fetchAllReturnedOrders = createAsyncThunk(
  "orders/fetchAllReturnedOrders",
  async (parameters: any, { rejectWithValue }) => {
    try {
      const data = await getAllReturnedOrders(parameters);
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data ?? "Failed to fetch returned orders"
      );
    }
  }
);

// Async thunk for fetching dispute reasons
export const fetchDisputeReasons = createAsyncThunk(
  "orders/fetchDisputeReasons",
  async (parameters: any, { rejectWithValue }) => {
    try {
      const data = await getDisputeReasons(parameters);
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data ?? "Failed to fetch dispute reasons"
      );
    }
  }
);

// Async thunk for fetching a dispute reason by ID
export const fetchDisputeReasonById = createAsyncThunk(
  "orders/fetchDisputeReasonById",
  async (id: any, { rejectWithValue }) => {
    try {
      const data = await getDisputeReasonById(id);
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data ?? "Failed to fetch dispute reason"
      );
    }
  }
);

// Async thunk for reviewing a dispute
export const disputeReview = createAsyncThunk(
  "orders/disputeReview",
  async (parameters: any, { rejectWithValue }) => {
    try {
      const data = await reviewDispute(parameters);
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data ?? "Failed to review dispute"
      );
    }
  }
);

// Async thunk for making a final decision on a dispute
export const finalDecision = createAsyncThunk(
  "orders/finalDecision",
  async (parameters: any, { rejectWithValue }) => {
    try {
      const data = await callFinalDecision(parameters);
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data ?? "Failed to make final decision"
      );
    }
  }
);

// Async thunk for making a final decision on a dispute
export const completeCourierPaymentAsync = createAsyncThunk(
  "orders/completeCourierPayment",
  async (parameters: any, { rejectWithValue }) => {
    try {
      const data = await completeCourierPayment(parameters);
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data ?? "Failed to complete confirmation"
      );
    }
  }
);

// Create the slice
const ordersSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    clearSelectedOrder: (state) => {
      state.selectedOrder = null;
    },
    setSelectedDispute: (state, action) => {
      state.selectedDispute = action.payload;
    },
    setSelectedReturnedOrder: (state, action) => {
      state.selectedReturnedOrder = action.payload;
    },
    setCurrentTable: (state, action) => {
      state.currentTable = action.payload;
    },
    clearOrders: (state) => {
      state.orders = null;
    },
  },
  extraReducers: (builder) => {
    // Handle fetchAllOrders
    builder
      .addCase(fetchAllOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAllOrders.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.orders = action.payload;
        }
      )
      .addCase(fetchAllOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Handle fetchOrderById
    builder
      .addCase(fetchOrderById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchOrderById.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.selectedOrder = action.payload;
        }
      )
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Handle fetchSingleDispute
    builder
      .addCase(fetchSingleDispute.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchSingleDispute.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.selectedDispute = action.payload.data;
        }
      )
      .addCase(fetchSingleDispute.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Handle updateOrderDropOff
    builder
      .addCase(updateOrderDropOff.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOrderDropOff.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateOrderDropOff.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    // Handle updateBulkOrderDropOff
    builder
      .addCase(updateBulkOrderDropOff.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBulkOrderDropOff.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateBulkOrderDropOff.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    // Handle updateOrderLocation
    builder
      .addCase(updateOrderLocation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOrderLocation.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateOrderLocation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    // Handle updateDispatchStatus
    builder
      .addCase(updateDispatchOrderStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDispatchOrderStatus.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateDispatchOrderStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Handle fetchAllDisputeOrders
    builder
      .addCase(fetchAllDisputeOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAllDisputeOrders.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.disputeOrders = action.payload; // Store dispute orders
        }
      )
      .addCase(fetchAllDisputeOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    // Handle fetchAllExecutedOrders
    builder
      .addCase(fetchAllExecutedOrdersAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAllExecutedOrdersAsync.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.executedOrders = action.payload; // Store dispute orders
        }
      )
      .addCase(fetchAllExecutedOrdersAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    // Handle fetchAllOrdersInTransit
    builder
      .addCase(fetchAllOrdersInTransitAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAllOrdersInTransitAsync.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.ordersInTransit = action.payload; // Store dispute orders
        }
      )
      .addCase(fetchAllOrdersInTransitAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    builder
      .addCase(fetchAllReturnedOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAllReturnedOrders.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.returnedOrders = action.payload; // Store Returned orders
        }
      )
      .addCase(fetchAllReturnedOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Handle fetchDisputeReasons
    builder
      .addCase(fetchDisputeReasons.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchDisputeReasons.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.disputeReasons = action.payload; // Store dispute reasons
        }
      )
      .addCase(fetchDisputeReasons.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Handle fetchDisputeReasonById
    builder
      .addCase(fetchDisputeReasonById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchDisputeReasonById.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.disputeReasonDetails = action.payload; // Store dispute reason details
        }
      )
      .addCase(fetchDisputeReasonById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    // Handle review dispute
    builder
      .addCase(disputeReview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(disputeReview.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(disputeReview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    // Handle final decision
    builder
      .addCase(finalDecision.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(finalDecision.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(finalDecision.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    // Handle final decision
    builder
      .addCase(completeCourierPaymentAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(completeCourierPaymentAsync.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(completeCourierPaymentAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    // Handle order summary
    builder
      .addCase(fetchOrderSummaryAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrderSummaryAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.orderSummary = action.payload;
      })
      .addCase(fetchOrderSummaryAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// Export actions and reducer
export const {
  clearSelectedOrder,
  setSelectedDispute,
  setCurrentTable,
  clearOrders,
  setSelectedReturnedOrder,
} = ordersSlice.actions;
export default ordersSlice.reducer;
